import { useState } from "react";
import type { BobState } from "../types";
import { createBuildIntake, validateIntake, type IntakeFormData } from "../lib/engine/buildIntake";
import { createDirectiveFromIntake, syncAgentsWithDirectives } from "../lib/engine/pipeline";

interface Props {
  state: BobState;
  onUpdate: (partial: Partial<BobState>) => void;
  toast: (msg: string) => void;
}

const initial: IntakeFormData = {
  projectName: "",
  projectType: "web-app",
  targetOutput: "web-app",
  stackPreference: "react-vite",
  sourceNotes: "",
  constraints: "No API paywall. Local-first.",
};

export function BuildIntakePanel({ state, onUpdate, toast }: Props) {
  const [form, setForm] = useState<IntakeFormData>(initial);
  const [errors, setErrors] = useState<string[]>([]);
  const compact = state.presentationMode;

  function submit() {
    const nextErrors = validateIntake(form);
    setErrors(nextErrors);
    if (nextErrors.length) return;
    const intake = createBuildIntake(form);
    const directive = createDirectiveFromIntake(intake);
    const directives = [...state.directives, directive];
    onUpdate({
      intakes: [...state.intakes, intake],
      directives,
      agents: syncAgentsWithDirectives(state.agents, directives),
    });
    toast(
      compact
        ? `Intake + pipeline: ${intake.projectName}`
        : `Intake registered + pipeline directive created: ${intake.projectName}`,
    );
    setForm({ ...initial, projectName: "", projectType: form.projectType });
  }

  return (
    <section className="surface glass-panel">
      <h2>Build Intake</h2>
      <p className="lead">
        {compact
          ? "Register build + pipeline directive."
          : "Register a sovereign build directive. Creates a linked pipeline directive automatically. No-API paywall toggle is locked ON."}
      </p>
      <div className="form-grid">
        <label>
          Project name
          <input
            value={form.projectName}
            onChange={(e) => setForm({ ...form, projectName: e.target.value })}
            placeholder="e.g. bob.arkmpyre upgrade"
          />
        </label>
        <label>
          Project type
          <input
            value={form.projectType}
            onChange={(e) => setForm({ ...form, projectType: e.target.value })}
            placeholder="coding assistant / worker / static site"
          />
        </label>
        <label>
          Target output
          <select
            value={form.targetOutput}
            onChange={(e) => setForm({ ...form, targetOutput: e.target.value as IntakeFormData["targetOutput"] })}
          >
            <option value="web-app">Web app</option>
            <option value="worker-api">Worker API</option>
            <option value="component-library">Component library</option>
            <option value="docs-site">Docs site</option>
            <option value="handoff-pack">Handoff pack</option>
          </select>
        </label>
        <label>
          Stack preference
          <select
            value={form.stackPreference}
            onChange={(e) => setForm({ ...form, stackPreference: e.target.value as IntakeFormData["stackPreference"] })}
          >
            <option value="react-vite">React + Vite</option>
            <option value="nextjs">Next.js</option>
            <option value="cloudflare-workers">Cloudflare Workers/Pages</option>
            <option value="static-html">Static HTML</option>
            <option value="unspecified">Unspecified</option>
          </select>
        </label>
        <label>
          Source files / folder notes
          <textarea
            value={form.sourceNotes}
            onChange={(e) => setForm({ ...form, sourceNotes: e.target.value })}
            placeholder="Paths, ZIP names, repo notes..."
          />
        </label>
        <label>
          Constraints
          <textarea
            value={form.constraints}
            onChange={(e) => setForm({ ...form, constraints: e.target.value })}
          />
        </label>
        <div>
          <span className="chip locked">No API / No paywall — LOCKED ON</span>
        </div>
      </div>
      {errors.length > 0 && (
        <div className="output-box" style={{ borderColor: "var(--accent-danger)" }}>
          {errors.join("\n")}
        </div>
      )}
      <div className="btn-row">
        <button type="button" className="btn gold" onClick={submit}>
          Register intake
        </button>
      </div>
      {state.intakes.length > 0 && (
        <div className="output-box">
          {state.intakes
            .slice(-3)
            .reverse()
            .map((i) => `[${i.createdAt.slice(0, 19)}] ${i.projectName} — ${i.stackPreference}`)
            .join("\n")}
        </div>
      )}
    </section>
  );
}
